import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NavigationHistoryService } from '../../core/services/navigation-history.service';
import { PermissionService } from '../../core/services/permission.service';
import { PERMISSION } from '../../core/configs/permissions.config';
import {
  BILLING_V2_PHASE_ENABLED,
  DEFAULT_REVAMP_FLAGS,
  RevampFlags,
  RevampService,
} from '../../core/services/revamp.service';

export interface NavMenuItem {
  label: string;
  icon?: string;
  routerLink?: string[];
  queryParams?: Record<string, string>;
  bgColor?: string;
  permission?: string;
  permissions?: string[];
  visible?: boolean;
  command?: () => void;
  items?: NavMenuItem[];
  separator?: boolean;
}

export interface NavRailSection {
  id: string;
  label: string;
  icon: string;
  bgColor: string;
  /** URL prefixes used to auto-select this rail from the current route */
  routePrefixes: string[];
  /** Primary page for leaf sections (also first shortcut when present) */
  routerLink?: string[];
  permission?: string;
  permissions?: string[];
  visible?: boolean;
  items: NavMenuItem[];
}

const DEFAULT_NAV_COLOR = '#64748b';

/** Extracts the hex from a Tailwind arbitrary class like `bg-[#2563EB]`. */
export function navColorFromBg(bgColor?: string | null): string {
  const match = /#([0-9a-f]{3,8})/i.exec(bgColor || '');
  return match ? `#${match[1]}` : DEFAULT_NAV_COLOR;
}

/** Longest-prefix rail match (exported for unit tests). */
export function matchRailIdFromPrefixes(
  url: string,
  sections: { id: string; routePrefixes: string[] }[]
): string | null {
  const path = (url || '').split('?')[0];
  let best: { id: string; len: number } | null = null;
  for (const section of sections) {
    for (const prefix of section.routePrefixes || []) {
      if (path === prefix || path.startsWith(prefix + '/')) {
        if (!best || prefix.length > best.len) {
          best = { id: section.id, len: prefix.length };
        }
      }
    }
  }
  return best?.id ?? null;
}

@Injectable({
  providedIn: 'root',
})
export class NavMenuService {
  private revampFlags: RevampFlags = { ...DEFAULT_REVAMP_FLAGS };
  private sectionsSubject = new BehaviorSubject<NavRailSection[]>([]);
  sections$ = this.sectionsSubject.asObservable();

  constructor(
    private navigationHistoryService: NavigationHistoryService,
    private permissionService: PermissionService,
    private revampService: RevampService
  ) {
    this.permissionService.permissionsChanged$.subscribe(() => this.refresh());
    this.load();
  }

  get sections(): NavRailSection[] {
    return this.sectionsSubject.value;
  }

  load(): void {
    this.revampService.getFlags().subscribe({
      next: flags => {
        this.revampFlags = flags;
        this.refresh();
      },
      error: () => this.refresh(),
    });
    this.refresh();
  }

  refresh(): void {
    const full = this.buildRailSections();
    this.sectionsSubject.next(this.filterSections(full));
  }

  getSectionById(id: string | null): NavRailSection | null {
    if (!id) return null;
    return this.sections.find(s => s.id === id) || null;
  }

  matchRailIdFromUrl(url: string): string | null {
    return matchRailIdFromPrefixes(url, this.sections);
  }

  private filterSections(sections: NavRailSection[]): NavRailSection[] {
    return sections
      .map(section => {
        if (section.visible === false) return null;
        const filteredItems = this.filterItems(section.items || []);
        const sectionAllowed =
          (!section.permission && !section.permissions) ||
          (section.permission &&
            this.permissionService.hasPermission(section.permission)) ||
          (section.permissions &&
            this.permissionService.hasAnyPermission(section.permissions));

        // Keep section if it has shortcuts OR it is a leaf with its own permission
        if (!sectionAllowed && filteredItems.length === 0) return null;
        if (filteredItems.length === 0 && !section.routerLink) return null;
        if (filteredItems.length === 0 && section.routerLink && !sectionAllowed) {
          return null;
        }

        return { ...section, items: filteredItems };
      })
      .filter((s): s is NavRailSection => s != null);
  }

  private filterItems(items: NavMenuItem[]): NavMenuItem[] {
    return items
      .map(item => {
        if (item.separator) return item;
        if (item.visible === false) return null;
        if (item.items) {
          const children = this.filterItems(item.items);
          const hasVisible = children.some(c => !c.separator && c.visible !== false);
          return hasVisible ? { ...item, items: children } : null;
        }
        const allowed =
          (!item.permission && !item.permissions) ||
          (item.permission &&
            this.permissionService.hasPermission(item.permission)) ||
          (item.permissions &&
            this.permissionService.hasAnyPermission(item.permissions));
        return allowed ? { ...item } : null;
      })
      .filter((item): item is NavMenuItem => item != null);
  }

  private buildRailSections(): NavRailSection[] {
    const nav = () => this.navigationHistoryService.removeAllValues();
    const canGenerateInvoice = this.permissionService.hasPermission(
      PERMISSION.INVOICE_GENERATE
    );
    const hideLegacy = !!this.revampFlags.hideClassicSalesMenus;
    const pipelinePrimary = !!this.revampFlags.pipelinePrimary;
    const softDeleteUi = !!this.revampFlags.softDeleteUi;
    const billingV2 = BILLING_V2_PHASE_ENABLED && !!this.revampFlags.billingV2;
    const opportunityHub = !!this.revampFlags.opportunityHub;
    const newForms = !!this.revampFlags.newClientJobForms;
    /** Soft-hide classic calendar entries when slide-out workspace is primary (routes stay). */
    const hideClassicCalendar =
      hideLegacy || !!this.revampFlags.calendarSlideout;

    const addClientLink = newForms
      ? ['/app/client/new']
      : ['/app/client/new'];
    const addInvoiceLink = billingV2
      ? ['/app/billing-v2/invoices/new']
      : ['/app/invoice/new'];

    return [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'pi pi-fw pi-th-large',
        bgColor: 'bg-[#FA8359]',
        routePrefixes: ['/app/dashboard'],
        routerLink: ['/app/dashboard'],
        permission: PERMISSION.DASHBOARD_VIEW_JOBS_BY_STATUS,
        items: [
          {
            label: 'Open Dashboard',
            icon: 'pi pi-fw pi-th-large',
            routerLink: ['/app/dashboard'],
            bgColor: 'bg-[#FA8359]',
            permission: PERMISSION.DASHBOARD_VIEW_JOBS_BY_STATUS,
            command: nav,
          },
          {
            label: 'Add Client',
            icon: 'pi pi-fw pi-user-plus',
            routerLink: addClientLink,
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#90BD79]',
            permission: PERMISSION.DASHBOARD_ADD_CLIENT,
            command: nav,
          },
          {
            label: 'Add Job',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/app/job/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#F8A56D]',
            permission: PERMISSION.DASHBOARD_ADD_JOB,
            command: nav,
          },
          {
            label: 'Add Invoice',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: addInvoiceLink,
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#9C68D0]',
            permission: PERMISSION.DASHBOARD_ADD_INVOICE,
            command: nav,
          },
        ],
      },
      {
        id: 'pipeline',
        label: pipelinePrimary ? 'Sales Pipeline' : 'Pipeline',
        icon: 'pi pi-fw pi-sitemap',
        bgColor: 'bg-[#2563EB]',
        routePrefixes: ['/app/pipeline'],
        routerLink: ['/app/pipeline'],
        permission: PERMISSION.PIPELINE_VIEW,
        items: [
          {
            label: 'Sales Pipeline',
            icon: 'pi pi-fw pi-sitemap',
            routerLink: ['/app/pipeline'],
            bgColor: 'bg-[#2563EB]',
            permission: PERMISSION.PIPELINE_VIEW,
            command: nav,
          },
          {
            label: 'Add Lead',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/app/pipeline/lead/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#475569]',
            permission: PERMISSION.LEAD_ADD,
            command: nav,
          },
          {
            label: 'Add Quotation',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/app/pipeline/quotation/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#14B8A6]',
            permission: PERMISSION.QUOTATION_ADD,
            command: nav,
          },
          {
            label: 'Add Job',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/app/pipeline/job/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#FB923C]',
            permission: PERMISSION.JOB_ADD,
            command: nav,
          },
        ],
      },
      {
        id: 'calendar',
        label: 'Calendar',
        icon: 'pi pi-fw pi-calendar',
        bgColor: 'bg-[#51ADCF]',
        routePrefixes: ['/app/schedule'],
        routerLink: ['/app/schedule'],
        permission: PERMISSION.CALENDAR_VIEW,
        items: [
          {
            label: 'Calendar Workspace',
            icon: 'pi pi-fw pi-calendar',
            routerLink: ['/app/schedule'],
            bgColor: 'bg-[#51ADCF]',
            permission: PERMISSION.CALENDAR_VIEW,
            command: nav,
          },
        ],
      },
      {
        id: 'clients',
        label: 'Clients',
        icon: 'pi pi-fw pi-users',
        bgColor: 'bg-[#90BD79]',
        routePrefixes: ['/app/client'],
        routerLink: ['/app/client'],
        items: [
          {
            label: 'View Clients',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/app/client'],
            bgColor: 'bg-[#90BD79]',
            permission: PERMISSION.CLIENT_VIEW_LIST,
            command: nav,
          },
          {
            label: 'Add Client',
            icon: 'pi pi-fw pi-user-plus',
            routerLink: ['/app/client/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#65A30D]',
            permission: PERMISSION.CLIENT_ADD,
            command: nav,
          },
        ],
      },
      {
        id: 'jobs',
        label: 'Jobs',
        icon: 'pi pi-fw pi-folder',
        bgColor: 'bg-[#F8A56D]',
        // Hub job detail lives under /app/pipeline/job — longer than /app/pipeline so Jobs wins.
        routePrefixes: ['/app/job', '/app/pipeline/job'],
        routerLink: ['/app/job'],
        items: [
          {
            label: 'View Jobs',
            icon: 'pi pi-fw pi-eye',
            routerLink: ['/app/job'],
            bgColor: 'bg-[#F8A56D]',
            permission: PERMISSION.JOB_VIEW_LIST,
            command: nav,
          },
          {
            label: 'Add Job',
            icon: 'pi pi-fw pi-plus-circle',
            routerLink: ['/app/job/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#FB923C]',
            permission: PERMISSION.JOB_ADD,
            command: nav,
          },
          {
            label: 'Time Clock',
            icon: 'pi pi-fw pi-clock',
            routerLink: ['/app/job/time-clock'],
            bgColor: 'bg-[#F59E0B]',
            permission: PERMISSION.TIMECLOCK_VIEW,
            command: nav,
          },
          {
            label: 'Approvals',
            icon: 'pi pi-fw pi-check-square',
            routerLink: ['/app/job/job-approvals'],
            bgColor: 'bg-[#EA580C]',
            permissions: [PERMISSION.TIMECLOCK_VIEW, PERMISSION.JOB_VIEW_LIST],
            command: nav,
          },
        ],
      },
      {
        id: 'invoices',
        label: billingV2 ? 'Billing' : 'Invoices',
        icon: 'pi pi-fw pi-file',
        bgColor: 'bg-[#9C68D0]',
        routePrefixes: billingV2
          ? ['/app/billing-v2', '/app/invoice', '/app/job-billing', '/app/billing']
          : ['/app/invoice', '/app/job-billing', '/app/billing'],
        routerLink: billingV2 ? ['/app/billing-v2'] : ['/app/invoice'],
        items: billingV2
          ? [
              {
                label: 'Jobs',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/app/billing-v2'],
                bgColor: 'bg-[#9C68D0]',
                permission: PERMISSION.INVOICE_VIEW_LIST,
                command: nav,
              },
              {
                label: 'Add Invoice',
                icon: 'pi pi-fw pi-plus-circle',
                routerLink: ['/app/billing-v2/invoices/new'],
                queryParams: { pre_pag: 'm#nu_!%em' },
                bgColor: 'bg-[#7C3AED]',
                permission: PERMISSION.INVOICE_ADD,
                command: nav,
              },
              {
                label: 'Billing Configs',
                icon: 'pi pi-fw pi-cog',
                routerLink: ['/app/billing-v2/configs'],
                bgColor: 'bg-[#8B5CF6]',
                permission: PERMISSION.BILLING_CONFIG_EDIT,
                command: nav,
              },
            ]
          : [
              {
                label: 'View Invoices',
                icon: 'pi pi-fw pi-folder-open',
                routerLink: ['/app/invoice'],
                bgColor: 'bg-[#9C68D0]',
                permission: PERMISSION.INVOICE_VIEW_LIST,
                command: nav,
              },
              {
                label: 'Add Invoice',
                icon: 'pi pi-fw pi-plus-circle',
                routerLink: ['/app/invoice/new'],
                queryParams: { pre_pag: 'm#nu_!%em' },
                bgColor: 'bg-[#7C3AED]',
                permission: PERMISSION.INVOICE_ADD,
                command: nav,
              },
              {
                label: 'Invoice Generator',
                icon: 'pi pi-fw pi-file',
                routerLink: ['/app/billing'],
                bgColor: 'bg-[#9C68D0]',
                visible: canGenerateInvoice && !billingV2,
                command: nav,
              },
            ],
      },
      {
        id: 'tasks',
        label: 'Tasks',
        icon: 'pi pi-fw pi-check-circle',
        bgColor: 'bg-[#6D68D0]',
        routePrefixes: ['/app/task'],
        routerLink: ['/app/task'],
        items: [
          {
            label: 'View Tasks',
            icon: 'pi pi-fw pi-eye',
            routerLink: ['/app/task'],
            bgColor: 'bg-[#6D68D0]',
            permission: PERMISSION.TASK_VIEW_LIST,
            command: nav,
          },
          {
            label: 'Add Task',
            icon: 'pi pi-fw pi-plus-circle',
            bgColor: 'bg-[#4F46E5]',
            routerLink: ['/app/task/new'],
            permission: PERMISSION.TASK_ADD,
            command: nav,
          },
        ],
      },
      {
        id: 'inventory',
        label: 'Inventory',
        icon: 'pi pi-fw pi-book',
        bgColor: 'bg-[#B4D068]',
        routePrefixes: ['/app/inventory'],
        routerLink: ['/app/inventory'],
        items: [
          {
            label: 'View Inventories',
            icon: 'pi pi-fw pi-folder-open',
            routerLink: ['/app/inventory'],
            bgColor: 'bg-[#B4D068]',
            permission: PERMISSION.INVENTORY_VIEW_LIST,
            command: nav,
          },
          {
            label: 'Add Inventory',
            icon: 'pi pi-fw pi-plus-circle',
            bgColor: 'bg-[#84CC16]',
            routerLink: ['/app/inventory/new'],
            permission: PERMISSION.INVENTORY_ADD,
            command: nav,
          },
        ],
      },
      {
        id: 'admin',
        label: 'Admin',
        icon: 'pi pi-fw pi-cog',
        bgColor: 'bg-[#68A4D0]',
        routePrefixes: [
          '/app/user',
          '/app/pipeline/deleted',
          '/app/billing',
          '/app/job-billing',
          '/app/holiday',
        ],
        routerLink: ['/app/user'],
        items: [
          {
            label: 'View Users',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/app/user'],
            bgColor: 'bg-[#68A4D0]',
            permission: PERMISSION.USER_VIEW_LIST,
            command: nav,
          },
          {
            label: 'Add User',
            icon: 'pi pi-fw pi-user-plus',
            routerLink: ['/app/user/new'],
            queryParams: { pre_pag: 'm#nu_!%em' },
            bgColor: 'bg-[#0284C7]',
            permission: PERMISSION.USER_ADD,
            command: nav,
          },
          {
            label: 'Permission Management',
            icon: 'pi pi-fw pi-lock',
            routerLink: ['/app/user/permissions'],
            bgColor: 'bg-[#0F766E]',
            permission: PERMISSION.PERMISSION_MANAGE,
            command: nav,
          },
          {
            label: 'Automatic breaks',
            icon: 'pi pi-fw pi-clock',
            routerLink: ['/app/user/revamp-settings'],
            bgColor: 'bg-[#6366F1]',
            permission: PERMISSION.PERMISSION_MANAGE,
            command: nav,
          },
          {
            label: 'Deleted records',
            icon: 'pi pi-fw pi-trash',
            routerLink: ['/app/pipeline/deleted'],
            bgColor: 'bg-[#EF4444]',
            visible: softDeleteUi && this.canViewDeletedRecords(),
            command: nav,
          },
          {
            label: 'Holiday Management',
            icon: 'pi pi-fw pi-calendar-times',
            routerLink: ['/app/holiday'],
            bgColor: 'bg-[#F43F5E]',
            permission: PERMISSION.HOLIDAY_MANAGE,
            command: nav,
          },
          {
            label: 'Common settings',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/app/user/common-settings'],
            bgColor: 'bg-[#0EA5E9]',
            permission: PERMISSION.HOLIDAY_MANAGE,
            command: nav,
          },
        ],
      },
      // {
      //   id: 'legacy',
      //   label: 'Legacy',
      //   icon: 'pi pi-fw pi-history',
      //   bgColor: 'bg-[#64748b]',
      //   routePrefixes: [
      //     '/app/lead',
      //     '/app/quotation',
      //     '/app/calendar',
      //     '/app/pipeline/job/new',
      //     '/app/client/add-edit',
      //     '/app/invoice/add-edit',
      //     '/app/user/add-edit',
      //   ],
      //   routerLink: ['/app/lead'],
      //   visible: !hideLegacy,
      //   items: [
      //     {
      //       label: 'Main Calendar',
      //       icon: 'pi pi-fw pi-calendar',
      //       routerLink: ['/app/calendar'],
      //       bgColor: 'bg-[#38BDF8]',
      //       permission: PERMISSION.CALENDAR_VIEW,
      //       visible: !hideClassicCalendar,
      //       command: nav,
      //     },
      //     {
      //       label: 'Resource Calendar',
      //       icon: 'pi pi-fw pi-calendar-plus',
      //       routerLink: ['/app/calendar/resource'],
      //       bgColor: 'bg-[#0EA5E9]',
      //       permission: PERMISSION.CALENDAR_VIEW,
      //       visible: !hideClassicCalendar,
      //       command: nav,
      //     },
      //     {
      //       label: 'View Leads',
      //       icon: 'pi pi-fw pi-eye',
      //       routerLink: ['/app/lead'],
      //       bgColor: 'bg-[#64748b]',
      //       permission: PERMISSION.LEAD_VIEW_LIST,
      //       command: nav,
      //     },
      //     {
      //       label: 'Add Lead',
      //       icon: 'pi pi-fw pi-plus-circle',
      //       routerLink: ['/app/pipeline/lead/new'],
      //       queryParams: { pre_pag: 'm#nu_!%em' },
      //       bgColor: 'bg-[#475569]',
      //       permission: PERMISSION.LEAD_ADD,
      //       command: nav,
      //     },
      //     {
      //       label: 'View Quotations',
      //       icon: 'pi pi-fw pi-folder-open',
      //       routerLink: ['/app/quotation'],
      //       bgColor: 'bg-[#0D9488]',
      //       permission: PERMISSION.QUOTATION_VIEW_LIST,
      //       command: nav,
      //     },
      //     {
      //       label: 'Add Quotation',
      //       icon: 'pi pi-fw pi-plus-circle',
      //       routerLink: ['/app/pipeline/quotation/new'],
      //       queryParams: { pre_pag: 'm#nu_!%em' },
      //       bgColor: 'bg-[#14B8A6]',
      //       permission: PERMISSION.QUOTATION_ADD,
      //       command: nav,
      //     },
      //     {
      //       label: 'Add Job',
      //       icon: 'pi pi-fw pi-folder',
      //       routerLink: ['/app/pipeline/job/new'],
      //       bgColor: 'bg-[#FB923C]',
      //       permission: PERMISSION.JOB_ADD,
      //       command: nav,
      //     },
      //     {
      //       label: 'Classic Client form',
      //       icon: 'pi pi-fw pi-user-plus',
      //       routerLink: ['/app/client/add-edit'],
      //       bgColor: 'bg-[#65A30D]',
      //       permission: PERMISSION.CLIENT_ADD,
      //       command: nav,
      //     },
      //     {
      //       label: 'Classic Invoice form',
      //       icon: 'pi pi-fw pi-file',
      //       routerLink: ['/app/invoice/add-edit'],
      //       bgColor: 'bg-[#7C3AED]',
      //       permission: PERMISSION.INVOICE_ADD,
      //       command: nav,
      //     },
      //     {
      //       label: 'Classic User form',
      //       icon: 'pi pi-fw pi-users',
      //       routerLink: ['/app/user/add-edit'],
      //       bgColor: 'bg-[#0284C7]',
      //       permission: PERMISSION.USER_ADD,
      //       command: nav,
      //     },
      //   ],
      // },
    ];
  }

  private canViewDeletedRecords(): boolean {
    return this.permissionService.hasAnyPermission([
      PERMISSION.SOFT_DELETE,
      PERMISSION.SOFT_DELETE_RESTORE,
    ]);
  }
}
